/**
 * Why Maya said what she said, for one turn.
 *
 * Shows the directive, what retrieval returned (including what it DROPPED and why), each guard's
 * verdict, the stage timings, and the token/rupee cost. This is the panel that makes the agent
 * arguable instead of magic — so it must show a rewrite or a block as prominently as a pass.
 *
 * ops/manager/owner only (src/lib/pii.ts canSeeExplain); reps never see prompt internals or cost.
 *
 * Author: Danish Khan
 */
import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { Explain } from '../lib/types.ts';
import { formatDuration, formatInr } from '../lib/format.ts';
import { Pill } from './Pill.tsx';

const VERDICT_TONE = { pass: 'ok', rewrite: 'warn', block: 'danger' } as const;

export function ExplainPanel({ explain }: { explain: Explain }): ReactNode {
  const fired = explain.verdicts.filter((v) => v.verdict !== 'pass');
  const totalMs = explain.stageLog.reduce((sum, s) => sum + s.ms, 0);

  return (
    <div className="flex flex-col gap-5 text-sm" data-testid="explain-panel">
      <section>
        <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">Directive</h3>
        <p className="rounded-[var(--h1-radius)] bg-surface-2 p-3 leading-relaxed text-fg">{explain.directive}</p>
      </section>

      <section>
        <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
          Retrieved ({explain.retrieved.length})
        </h3>
        {explain.retrieved.length === 0 ? (
          <p className="text-fg-muted">Nothing was retrieved for this turn — no product was in play.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {explain.retrieved.map((r) => (
              <li key={r.productId} className="flex items-start gap-2 rounded-md border border-line p-2">
                <span className="mt-0.5 font-mono text-[11px] tabular-nums text-fg-subtle">
                  {r.score.toFixed(2)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-fg">{r.title}</span>
                  <span className="block text-[11px] text-fg-muted">{r.why}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="mb-1.5 flex items-center gap-2 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
          Guards
          {fired.length > 0 && (
            <Pill tone="warn">
              {fired.length} fired
            </Pill>
          )}
        </h3>
        <ul className="flex flex-col gap-1.5">
          {explain.verdicts.map((v) => (
            <li key={v.guard} className="flex items-start gap-2">
              <Pill tone={VERDICT_TONE[v.verdict]}>{v.verdict}</Pill>
              <span className="min-w-0 flex-1">
                <span className="block font-mono text-[11px] text-fg">{v.guard}</span>
                <span className="block text-[11px] text-fg-muted">{v.note}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
          Stages ({formatDuration(totalMs)})
        </h3>
        <ul className="flex flex-col gap-1">
          {explain.stageLog.map((s) => (
            <li key={s.stage} className="flex items-center gap-2">
              <span className="w-16 shrink-0 font-mono text-[11px] text-fg">{s.stage}</span>
              <span
                aria-hidden
                className={clsx('h-1.5 rounded-full bg-brand')}
                style={{ width: `${Math.max(3, Math.round((s.ms / Math.max(1, totalMs)) * 140))}px` }}
              />
              <span className="shrink-0 font-mono text-[11px] tabular-nums text-fg-subtle">{s.ms} ms</span>
              <span className="min-w-0 flex-1 truncate text-[11px] text-fg-muted">{s.note}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="flex flex-wrap gap-x-6 gap-y-1 border-t border-line pt-3 text-[11px] text-fg-muted">
        <span>
          model <span className="font-mono text-fg">{explain.model}</span>
        </span>
        <span>
          tokens <span className="font-mono text-fg tabular-nums">{explain.tokens.in} in / {explain.tokens.out} out</span>
        </span>
        <span>
          cost <span className="font-mono text-fg tabular-nums">{formatInr(explain.costInr, true)}</span>
        </span>
        <span>
          turn <span className="font-mono text-fg">{explain.turnId}</span>
        </span>
      </section>
    </div>
  );
}
